// Arm the part rig's facial joints as the head's vertex source (SyntheticOGSFace).
// Notes: ~/comments/aos-threejs-poc/syntheticFace_armSyntheticFace_two_conventions.md

import {
  SYNTHETIC_FACE_PACK, SYNTHETIC_FACE_DEV_PACK_URL, createSyntheticOgsFace,
} from "../rig/syntheticOgsFace.js";
import type { OgsLogger } from "./ogsLog.ts";

export interface SyntheticFaceHandle {
  /** Feed one ARKit-52 frame. Change-gated: an identical frame poses nothing. */
  setArkit(arkit: Float32Array): boolean;
  /** Facial joints bound on the part rig. */
  matched: number;
  /** Where the pack came from, for the debug seam. */
  packSource: string;
  release(): void;
}

export interface ArmSyntheticFaceInput {
  /** The bundle's byte source — the pack rides with the character. */
  fetchBytes(name: string): Promise<Uint8Array | ArrayBuffer>;
  /** The part rig's scene root, whose bones the joints pose. */
  sceneRoot: unknown;
  /** Called after a frame changed the joints — the pipeline re-skins the head. */
  onPosed: () => void;
  log: OgsLogger;
  /** Test seam for the local dev asset fetch. */
  fetchUrl?: typeof fetch;
}

/** Null when the character ships no pack, with the reason on the console. Never throws. */
export async function armSyntheticFace(input: ArmSyntheticFaceInput): Promise<SyntheticFaceHandle | null> {
  const { log } = input;
  const fetchUrl = input.fetchUrl ?? (typeof fetch === "function" ? fetch : null);
  let packBytes: Uint8Array | null = null;
  let packSource = `bundle ${SYNTHETIC_FACE_PACK}`;
  try {
    packBytes = new Uint8Array(await input.fetchBytes(SYNTHETIC_FACE_PACK));
  } catch {
    // The local dev asset is the authoring loop's copy — never a production fallback.
    if (!fetchUrl) {
      log.error(`no ${SYNTHETIC_FACE_PACK} in the bundle and no fetch to try the dev asset — face stays rigid`);
      return null;
    }
    const response = await fetchUrl(SYNTHETIC_FACE_DEV_PACK_URL);
    if (!response.ok) {
      log.error(`no ${SYNTHETIC_FACE_PACK} in the bundle and ${SYNTHETIC_FACE_DEV_PACK_URL} `
        + `answered ${response.status} — the face stays rigid`);
      return null;
    }
    packBytes = new Uint8Array(await response.arrayBuffer());
    packSource = `LOCAL DEV ASSET ${SYNTHETIC_FACE_DEV_PACK_URL}`;
  }
  log.info(`synthetic face pack: ${packSource}`);
  const face = await createSyntheticOgsFace({
    packBytes,
    sceneRoot: input.sceneRoot,
    onPosed: input.onPosed,
    log: (message: string) => log.info(message),
  });
  if (!face) return null;
  return { setArkit: face.setArkit, matched: face.matched, packSource, release: face.release };
}
